import { useEffect, useState } from 'react'
import { api, money, fmtDate, ApiError } from '../api'
import { Page, useAuth } from '../App'
import { Card, Alert, Spinner, Tabs, Field, Badge, Modal, Empty } from '../components/ui'
import { rungLabel } from '@shared/domain/reminderLadder'
import { describeCin } from '@shared/lib/validate'

export default function Settings() {
  const [tab, setTab] = useState('company')
  return (
    <Page title="Settings">
      <Tabs
        tabs={[
          { key: 'company', label: 'Company' },
          { key: 'numbering', label: 'Numbering' },
          { key: 'email', label: 'Email providers' },
          { key: 'reminders', label: 'Reminders' },
          { key: 'templates', label: 'Templates' },
          { key: 'account', label: 'Account' },
          { key: 'audit', label: 'Audit & jobs' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'company' && <Company />}
      {tab === 'numbering' && <Numbering />}
      {tab === 'email' && <EmailProviders />}
      {tab === 'reminders' && <Reminders />}
      {tab === 'templates' && <Templates />}
      {tab === 'account' && <Account />}
      {tab === 'audit' && <AuditAndJobs />}
    </Page>
  )
}

// ------------------------------------------------------------------ company

function Company() {
  const [c, setC] = useState<Record<string, string | number | null> | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.get<{ company: Record<string, string | number | null> }>('/settings/company').then((r) => setC(r.company))
  }, [])

  if (!c) return <Spinner />
  const set = (k: string, v: unknown) => setC({ ...c, [k]: v as string })
  const val = (k: string) => String(c[k] ?? '')
  const isPlaceholder = (k: string) => val(k).startsWith('PLACEHOLDER')

  const save = async () => {
    setError('')
    setNotice('')
    setBusy(true)
    try {
      await api.put('/settings/company', c)
      setNotice('Company profile saved.')
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  const upload = async (kind: 'logo' | 'signature', file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    try {
      await api.post(`/settings/upload/${kind}`, fd)
      setNotice(`${kind === 'logo' ? 'Logo' : 'Signature'} uploaded.`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Upload failed')
    }
  }

  const placeholders = [
    'email', 'phone', 'website', 'bank_name', 'bank_account', 'bank_ifsc',
    'bank_branch', 'upi_vpa', 'lut_number', 'swift_code',
  ].filter(isPlaceholder)

  return (
    <>
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{notice}</Alert>

      {placeholders.length > 0 && (
        <Alert kind="warn">
          <strong>{placeholders.length} field{placeholders.length === 1 ? '' : 's'} still hold placeholder values</strong> —
          they are hidden from the invoice until you fill them in: {placeholders.join(', ')}.
        </Alert>
      )}

      <Card title="Identity" actions={<button className="btn primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>}>
        <div className="grid c2">
          <Field label="Legal name">
            <input type="text" value={val('legal_name')} onChange={(e) => set('legal_name', e.target.value)} />
          </Field>
          <Field label="Trade name">
            <input type="text" value={val('trade_name')} onChange={(e) => set('trade_name', e.target.value)} />
          </Field>
          <Field label="GSTIN" hint="Checksum-validated. The state code is derived from it.">
            <input type="text" className="mono" value={val('gstin')} onChange={(e) => set('gstin', e.target.value.toUpperCase())} />
          </Field>
          <Field label="PAN">
            <input type="text" className="mono" value={val('pan')} onChange={(e) => set('pan', e.target.value.toUpperCase())} />
          </Field>
          <Field
            label="CIN"
            hint={
              describeCin(val('cin')) ??
              (val('cin')
                ? 'Does not parse as a valid 21-character CIN'
                : 'Required on invoices under Companies Act s.12(3)(c)')
            }
          >
            <input
              type="text"
              className="mono"
              maxLength={21}
              value={val('cin')}
              onChange={(e) => set('cin', e.target.value.toUpperCase())}
            />
          </Field>
          <Field label="Email">
            <input type="email" value={val('email')} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label="Phone">
            <input type="text" value={val('phone')} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label="Website">
            <input type="text" value={val('website')} onChange={(e) => set('website', e.target.value)} />
          </Field>
        </div>

        <div className="sep" />
        <h3 style={{ marginBottom: 10 }}>Registered address</h3>
        <div className="grid c2">
          <Field label="Address line 1">
            <input type="text" value={val('addr_line1')} onChange={(e) => set('addr_line1', e.target.value)} />
          </Field>
          <Field label="Address line 2">
            <input type="text" value={val('addr_line2')} onChange={(e) => set('addr_line2', e.target.value)} />
          </Field>
          <Field label="City">
            <input type="text" value={val('city')} onChange={(e) => set('city', e.target.value)} />
          </Field>
          <Field label="PIN code">
            <input type="text" value={val('pincode')} onChange={(e) => set('pincode', e.target.value)} />
          </Field>
        </div>
        <div className="alert info small">
          State: <strong>{val('state') || '—'} ({val('state_code') || '—'})</strong>, taken from the GSTIN.
          This is the supplier state that decides CGST+SGST versus IGST on every invoice.
        </div>
      </Card>

      <div style={{ height: 14 }} />

      <Card title="Payment details" actions={<button className="btn primary" onClick={save} disabled={busy}>Save</button>}>
        <div className="grid c2">
          <Field label="Account name">
            <input type="text" value={val('bank_account_name')} onChange={(e) => set('bank_account_name', e.target.value)} />
          </Field>
          <Field label="Account number">
            <input type="text" className="mono" value={val('bank_account')} onChange={(e) => set('bank_account', e.target.value)} />
          </Field>
          <Field label="IFSC">
            <input type="text" className="mono" value={val('bank_ifsc')} onChange={(e) => set('bank_ifsc', e.target.value.toUpperCase())} />
          </Field>
          <Field label="Bank name">
            <input type="text" value={val('bank_name')} onChange={(e) => set('bank_name', e.target.value)} />
          </Field>
          <Field label="Branch">
            <input type="text" value={val('bank_branch')} onChange={(e) => set('bank_branch', e.target.value)} />
          </Field>
          <Field label="SWIFT code" hint="Needed for inward remittances on export invoices.">
            <input type="text" className="mono" value={val('swift_code')} onChange={(e) => set('swift_code', e.target.value)} />
          </Field>
          <Field label="UPI VPA" hint="A QR is printed on every INR invoice with the amount pre-filled.">
            <input type="text" className="mono" value={val('upi_vpa')} onChange={(e) => set('upi_vpa', e.target.value)} placeholder="yourname@bank" />
          </Field>
          <Field label="UPI payee name">
            <input type="text" value={val('upi_name')} onChange={(e) => set('upi_name', e.target.value)} />
          </Field>
        </div>
      </Card>

      <div style={{ height: 14 }} />

      <Card title="Exports (LUT)" actions={<button className="btn primary" onClick={save} disabled={busy}>Save</button>}>
        <p className="muted small" style={{ marginTop: 0 }}>
          Required on zero-rated export invoices issued without payment of IGST.
        </p>
        <div className="grid c3">
          <Field label="LUT ARN">
            <input type="text" className="mono" value={val('lut_number')} onChange={(e) => set('lut_number', e.target.value)} />
          </Field>
          <Field label="Valid from">
            <input type="date" value={val('lut_valid_from')} onChange={(e) => set('lut_valid_from', e.target.value)} />
          </Field>
          <Field label="Valid to">
            <input type="date" value={val('lut_valid_to')} onChange={(e) => set('lut_valid_to', e.target.value)} />
          </Field>
        </div>
      </Card>

      <div style={{ height: 14 }} />

      <Card title="Invoice appearance & defaults" actions={<button className="btn primary" onClick={save} disabled={busy}>Save</button>}>
        <div className="grid c3">
          <Field label="Accent colour">
            <input type="color" value={val('theme_color') || '#1f3a8a'} onChange={(e) => set('theme_color', e.target.value)} />
          </Field>
          <Field label="Default payment terms (days)">
            <input type="number" value={val('default_payment_terms_days')} onChange={(e) => set('default_payment_terms_days', Number(e.target.value))} />
          </Field>
          <Field label="HSN/SAC digits required" hint="4 below ₹5cr turnover, 6 at or above.">
            <select value={val('hsn_digits')} onChange={(e) => set('hsn_digits', Number(e.target.value))}>
              <option value="4">4 digits</option>
              <option value="6">6 digits</option>
              <option value="8">8 digits</option>
            </select>
          </Field>
        </div>

        <div className="grid c2">
          <Field label="Logo" hint="PNG, JPEG, WebP or SVG, under 2 MB.">
            <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && upload('logo', e.target.files[0])} />
            {val('logo_key') && <img src="/api/settings/asset/logo" alt="" style={{ maxHeight: 50, marginTop: 8 }} />}
          </Field>
          <Field label="Signature image">
            <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && upload('signature', e.target.files[0])} />
            {val('signature_key') && <img src="/api/settings/asset/signature" alt="" style={{ maxHeight: 50, marginTop: 8 }} />}
          </Field>
        </div>

        <div className="grid c2">
          <Field
            label="Signatory name"
            hint="Printed as a typed signature when no signature image is uploaded."
          >
            <input type="text" value={val('signatory_name')} onChange={(e) => set('signatory_name', e.target.value)} />
          </Field>
          <Field label="Designation">
            <input
              type="text"
              value={val('signatory_designation')}
              onChange={(e) => set('signatory_designation', e.target.value)}
              placeholder="Director"
            />
          </Field>
        </div>

        <label className="check" style={{ marginBottom: 12 }}>
          <input type="checkbox" checked={Number(c.show_signature) === 1} onChange={(e) => set('show_signature', e.target.checked ? 1 : 0)} />
          Print the signature image on invoices (falls back to the typed name above)
        </label>

        <Field label="Default terms & conditions">
          <textarea value={val('default_terms')} onChange={(e) => set('default_terms', e.target.value)} />
        </Field>
      </Card>
    </>
  )
}

// ----------------------------------------------------------------- numbering

function Numbering() {
  const [data, setData] = useState<{ series: Record<string, string | number>[]; currentFy: number } | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = () => api.get<never>('/settings/numbering').then((d) => setData(d as never))
  useEffect(() => {
    load()
  }, [])

  if (!data) return <Spinner />

  return (
    <>
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{notice}</Alert>
      <Alert kind="info">
        The scheme is <span className="mono">prefix + number</span>, with each financial year starting a
        new 100-number block: FY {data.currentFy}-{String((data.currentFy + 1) % 100).padStart(2, '0')} runs
        from <span className="mono">DigiAud2600</span>, and FY {data.currentFy + 1} rolls to{' '}
        <span className="mono">DigiAud2700</span>. If a year ever exceeds its block, the next year starts
        above the highest number used — an invoice number is never reused.
      </Alert>

      <Card tight>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Series</th>
                <th>Prefix</th>
                <th>FY</th>
                <th className="r">Next number</th>
                <th className="r">Used / capacity</th>
                <th>Last issued</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.series.map((s) => {
                const used = Number(s.next_number) - Number(s.base)
                const cap = Number(s.capacity)
                return (
                  <tr key={String(s.series_key)}>
                    <td>{String(s.label)}</td>
                    <td className="mono">{String(s.prefix)}</td>
                    <td>{String(s.fy_start_year)}</td>
                    <td className="num mono">
                      <strong>
                        {String(s.prefix)}
                        {String(s.next_number)}
                      </strong>
                    </td>
                    <td className="num">
                      <span style={{ color: used >= cap - 10 ? 'var(--danger)' : undefined }}>
                        {used} / {cap}
                      </span>
                    </td>
                    <td className="mono small">{String(s.last_issued ?? '—')}</td>
                    <td className="right">
                      <button
                        className="btn sm"
                        onClick={async () => {
                          const v = prompt(
                            `Resume the ${s.label} series from which number?\n\nUse this once at go-live to continue an existing sequence.`,
                            String(s.next_number),
                          )
                          if (!v) return
                          try {
                            await api.post(`/settings/numbering/${s.series_key}`, { next_number: Number(v) })
                            setNotice(`${s.label} will resume from ${s.prefix}${v}.`)
                            load()
                          } catch (e) {
                            setError(e instanceof ApiError ? e.message : 'Failed')
                          }
                        }}
                      >
                        Set next
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  )
}

// ----------------------------------------------------------- email providers

const KIND_LABELS: Record<string, string> = {
  manual: 'Manual (copy / paste)',
  cf_email: 'Cloudflare Email Service',
  custom_api: 'Custom HTTP API',
  smtp: 'SMTP',
}

function EmailProviders() {
  const [data, setData] = useState<{
    providers: Record<string, string | number | boolean>[]
    secretConfigured: boolean
    presets: Record<string, unknown>
  } | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [adding, setAdding] = useState(false)
  const [log, setLog] = useState<Record<string, string | number | null>[]>([])

  const load = async () => {
    setData(await api.get<never>('/settings/email/providers'))
    setLog((await api.get<{ log: Record<string, string | number | null>[] }>('/settings/email/log')).log)
  }

  useEffect(() => {
    load()
  }, [])

  if (!data) return <Spinner />

  return (
    <>
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{notice}</Alert>

      {!data.secretConfigured && (
        <Alert kind="warn">
          <strong>SECRET_KEY is not set.</strong> Provider credentials are encrypted with it, so SMTP
          and API providers cannot be saved until you run{' '}
          <span className="mono">wrangler secret put SECRET_KEY</span>. Manual sending works without it.
        </Alert>
      )}

      <Alert kind="info">
        Providers are tried in priority order, lowest first, and the next one is used if a send fails.
        <strong> Manual always succeeds without transmitting anything</strong> — so while it sits at the
        top, nothing is emailed automatically and you send from your own mailbox via the Email kit.
      </Alert>

      <Card
        title="Providers"
        actions={
          <button className="btn primary sm" onClick={() => setAdding(true)}>
            + Add provider
          </button>
        }
        tight
      >
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>From</th>
                <th className="r">Priority</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.providers.map((p) => (
                <tr key={String(p.id)}>
                  <td>{String(p.name)}</td>
                  <td className="small">{KIND_LABELS[String(p.kind)] ?? String(p.kind)}</td>
                  <td className="small">{String(p.from_email ?? '—')}</td>
                  <td className="num">{Number(p.priority)}</td>
                  <td>
                    {p.is_active ? <Badge status="paid">Active</Badge> : <Badge status="draft">Off</Badge>}
                    {p.last_error ? (
                      <div className="small" style={{ color: 'var(--danger)', marginTop: 3 }}>
                        {String(p.last_error).slice(0, 70)}
                      </div>
                    ) : null}
                  </td>
                  <td className="right nowrap">
                    <button
                      className="btn sm"
                      onClick={async () => {
                        await api.patch(`/settings/email/providers/${p.id}`, { is_active: !p.is_active })
                        load()
                      }}
                    >
                      {p.is_active ? 'Disable' : 'Enable'}
                    </button>{' '}
                    <button
                      className="btn sm"
                      onClick={async () => {
                        const to = prompt('Send a test email to which address?')
                        if (!to) return
                        try {
                          const r = await api.post<{ ok: boolean; manual: boolean; error?: string }>(
                            `/settings/email/providers/${p.id}/test`,
                            { to },
                          )
                          setNotice(
                            r.manual
                              ? 'Manual provider — nothing was transmitted, by design.'
                              : r.ok
                                ? `Test sent to ${to}.`
                                : '',
                          )
                          if (!r.ok && !r.manual) setError(r.error ?? 'Test failed')
                        } catch (e) {
                          setError(e instanceof ApiError ? e.message : 'Test failed')
                        }
                        load()
                      }}
                    >
                      Test
                    </button>{' '}
                    {String(p.kind) !== 'manual' && (
                      <button
                        className="btn sm danger"
                        onClick={async () => {
                          await api.del(`/settings/email/providers/${p.id}`)
                          load()
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div style={{ height: 14 }} />

      <Card title="Recent email log" tight>
        {log.length === 0 ? (
          <Empty title="No emails yet" />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Invoice</th>
                  <th>Kind</th>
                  <th>To</th>
                  <th>Subject</th>
                  <th>Via</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {log.slice(0, 40).map((e) => (
                  <tr key={String(e.id)}>
                    <td className="small nowrap">{String(e.created_at).slice(0, 16).replace('T', ' ')}</td>
                    <td className="mono small">{String(e.invoice_number ?? '—')}</td>
                    <td className="small">{String(e.kind)}</td>
                    <td className="small">{(JSON.parse(String(e.to_json)) as string[]).join(', ')}</td>
                    <td className="small">{String(e.subject).slice(0, 48)}</td>
                    <td className="small">{String(e.provider_kind ?? '—')}</td>
                    <td>
                      <Badge status={e.status === 'sent' ? 'paid' : e.status === 'failed' ? 'overdue' : 'draft'}>
                        {String(e.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {adding && (
        <ProviderForm
          presets={data.presets}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false)
            load()
          }}
        />
      )}
    </>
  )
}

function ProviderForm({
  presets,
  onClose,
  onSaved,
}: {
  presets: Record<string, unknown>
  onClose: () => void
  onSaved: () => void
}) {
  const [kind, setKind] = useState('smtp')
  const [name, setName] = useState('Google Workspace (SMTP)')
  const [fromName, setFromName] = useState('DIGIAUD (OPC) PRIVATE LIMITED')
  const [fromEmail, setFromEmail] = useState('hello@digiaud.in')
  const [replyTo, setReplyTo] = useState('')
  const [priority, setPriority] = useState('10')
  const [config, setConfig] = useState('{}')
  const [error, setError] = useState('')

  // Structured SMTP fields, so a password is never typed into a JSON blob.
  const [smtpHost, setSmtpHost] = useState('smtp.gmail.com')
  const [smtpPort, setSmtpPort] = useState('587')
  const [smtpUser, setSmtpUser] = useState('hello@digiaud.in')
  const [smtpPass, setSmtpPass] = useState('')

  useEffect(() => {
    if (kind === 'smtp') {
      setName('Google Workspace (SMTP)')
    } else if (kind === 'custom_api') {
      setConfig(JSON.stringify(presets.resend, null, 2))
      setName('Resend (custom API)')
    } else if (kind === 'cf_email') {
      setConfig('{}')
      setName('Cloudflare Email Service')
    } else {
      setConfig('{}')
      setName('Manual')
    }
  }, [kind, presets])

  const buildConfig = () =>
    kind === 'smtp'
      ? {
          host: smtpHost.trim(),
          port: Number(smtpPort) || 587,
          username: smtpUser.trim(),
          password: smtpPass,
        }
      : JSON.parse(config)

  return (
    <Modal
      wide
      title="Add email provider"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={async () => {
              setError('')
              try {
                await api.post('/settings/email/providers', {
                  kind,
                  name,
                  from_name: fromName,
                  from_email: fromEmail,
                  reply_to: replyTo || fromEmail,
                  priority: Number(priority),
                  is_active: true,
                  config: buildConfig(),
                })
                onSaved()
              } catch (e) {
                setError(e instanceof ApiError ? e.message : 'Could not save — check the JSON config')
              }
            }}
          >
            Add provider
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>

      <Field label="Type">
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(KIND_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </Field>

      {kind === 'smtp' && (
        <Alert kind="info">
          For Google Workspace use <span className="mono">smtp.gmail.com</span> on port{' '}
          <strong>587</strong> with an <strong>App Password</strong> — not your normal account
          password. Generate one at Google Account → Security → App passwords. Workers only supports
          STARTTLS on 587; port 25 is blocked and implicit TLS on 465 will not work.
        </Alert>
      )}
      {kind === 'cf_email' && (
        <Alert kind="info">
          Needs digiaud.in on Cloudflare DNS with SPF and DKIM published, plus the{' '}
          <span className="mono">send_email</span> binding uncommented in wrangler.jsonc.
        </Alert>
      )}

      <div className="grid c2">
        <Field label="Display name">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Priority" hint="Lower runs first. Manual is 10 by default.">
          <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} />
        </Field>
        <Field label="From name">
          <input type="text" value={fromName} onChange={(e) => setFromName(e.target.value)} />
        </Field>
        <Field label="From email">
          <input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
        </Field>
      </div>
      <Field label="Reply-To">
        <input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="Defaults to the From address" />
      </Field>

      {kind === 'smtp' && (
        <>
          <div className="sep" />
          <h3 style={{ marginBottom: 10 }}>SMTP server</h3>
          <div className="grid c2">
            <Field label="Host">
              <input type="text" className="mono" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} />
            </Field>
            <Field label="Port" hint="587 only.">
              <input type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} />
            </Field>
            <Field label="Username" hint="Usually the full mailbox address.">
              <input type="text" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} />
            </Field>
            <Field
              label="App password"
              hint="Encrypted with AES-GCM before it is stored, and never returned by the API."
            >
              <input
                type="password"
                autoComplete="new-password"
                value={smtpPass}
                onChange={(e) => setSmtpPass(e.target.value)}
                placeholder="xxxx xxxx xxxx xxxx"
              />
            </Field>
          </div>
        </>
      )}

      {kind !== 'manual' && kind !== 'smtp' && (
        <Field
          label="Configuration (JSON)"
          hint="Encrypted with AES-GCM before it touches the database, and never returned by the API."
        >
          <textarea className="mono" rows={12} value={config} onChange={(e) => setConfig(e.target.value)} />
        </Field>
      )}
    </Modal>
  )
}

// ----------------------------------------------------------------- reminders

function Reminders() {
  const [data, setData] = useState<{ rules: Record<string, string | number>[]; enabled: boolean } | null>(null)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  const load = () => api.get<never>('/settings/reminders').then((d) => setData(d as never))
  useEffect(() => {
    load()
  }, [])

  if (!data) return <Spinner />
  const rule = data.rules[0]
  const offsets: { offset: number; template: string }[] = rule ? JSON.parse(String(rule.offsets_json)) : []

  return (
    <>
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{notice}</Alert>

      <Card
        title="Automatic payment reminders"
        actions={
          <button
            className={`btn ${data.enabled ? 'danger' : 'primary'}`}
            onClick={async () => {
              await api.post('/settings/toggle/reminders_enabled', { value: !data.enabled })
              setNotice(data.enabled ? 'Reminders turned off globally.' : 'Reminders turned on.')
              load()
            }}
          >
            {data.enabled ? 'Turn off globally' : 'Turn on'}
          </button>
        }
      >
        <p className="muted small" style={{ marginTop: 0 }}>
          The sweep runs at 09:00 IST daily over every unpaid invoice. It fires the latest rung that is
          due but not yet sent, so a backlog produces one email, not five. A unique index on
          (invoice, offset) means a repeated run physically cannot email a client twice.
        </p>

        {rule && (
          <>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Template</th>
                  </tr>
                </thead>
                <tbody>
                  {offsets.map((o, i) => (
                    <tr key={i}>
                      <td>{rungLabel(o.offset)}</td>
                      <td className="mono small">{o.template}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sep" />
            <div className="grid c4">
              <Field label="Quiet hours start">
                <input
                  type="number"
                  min="0"
                  max="23"
                  defaultValue={Number(rule.quiet_start_hour)}
                  onBlur={(e) => api.patch(`/settings/reminders/${rule.id}`, { quiet_start_hour: Number(e.target.value) })}
                />
              </Field>
              <Field label="Quiet hours end">
                <input
                  type="number"
                  min="0"
                  max="23"
                  defaultValue={Number(rule.quiet_end_hour)}
                  onBlur={(e) => api.patch(`/settings/reminders/${rule.id}`, { quiet_end_hour: Number(e.target.value) })}
                />
              </Field>
              <Field label="Max reminders per invoice">
                <input
                  type="number"
                  defaultValue={Number(rule.max_reminders)}
                  onBlur={(e) => api.patch(`/settings/reminders/${rule.id}`, { max_reminders: Number(e.target.value) })}
                />
              </Field>
              <Field label="Business days only">
                <select
                  defaultValue={String(rule.business_days_only)}
                  onChange={(e) => api.patch(`/settings/reminders/${rule.id}`, { business_days_only: e.target.value === '1' })}
                >
                  <option value="0">Any day</option>
                  <option value="1">Weekdays only</option>
                </select>
              </Field>
            </div>
            <div className="hint">
              Quiet hours wrap midnight: nothing is sent between {String(rule.quiet_start_hour)}:00 and{' '}
              {String(rule.quiet_end_hour)}:00 IST.
            </div>
          </>
        )}
      </Card>
    </>
  )
}

// ----------------------------------------------------------------- templates

function Templates() {
  const [templates, setTemplates] = useState<Record<string, string | number | null>[]>([])
  const [editing, setEditing] = useState<Record<string, string | number | null> | null>(null)
  const [notice, setNotice] = useState('')

  const load = () =>
    api.get<{ templates: Record<string, string | number | null>[] }>('/settings/email/templates').then((r) => setTemplates(r.templates))

  useEffect(() => {
    load()
  }, [])

  return (
    <>
      <Alert kind="ok">{notice}</Alert>
      <Card tight>
        <table className="tbl">
          <thead>
            <tr>
              <th>Template</th>
              <th>Subject</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={String(t.id)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{String(t.name)}</div>
                  <div className="mono small muted">{String(t.key)}</div>
                </td>
                <td className="small">{String(t.subject_tpl)}</td>
                <td className="right">
                  <button className="btn sm" onClick={() => setEditing(t)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {editing && (
        <Modal
          wide
          title={`Edit: ${editing.name}`}
          onClose={() => setEditing(null)}
          footer={
            <>
              <button className="btn" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={async () => {
                  await api.patch(`/settings/email/templates/${editing.id}`, {
                    subject_tpl: editing.subject_tpl,
                    body_text_tpl: editing.body_text_tpl,
                    body_html_tpl: editing.body_html_tpl,
                  })
                  setEditing(null)
                  setNotice('Template saved.')
                  load()
                }}
              >
                Save
              </button>
            </>
          }
        >
          <Alert kind="info">
            <div style={{ marginBottom: 6 }}>
              <strong>
                <span className="mono">{'{{invoice_full_html}}'}</span>
              </strong>{' '}
              drops the entire invoice into the HTML body — line items, tax split, HSN summary,
              payments, bank details, UPI QR and the signatory block. Use{' '}
              <span className="mono">{'{{invoice_full_text}}'}</span> in the plain-text body.
            </div>
            <div style={{ marginBottom: 6 }}>
              <span className="mono">{'{{contact.name}}'}</span> is the person on the To line when
              there is exactly one, otherwise the client name — so a greeting is never blank.
            </div>
            Other variables:{' '}
            <span className="mono">
              {'{{contact.name}} {{client.name}} {{invoice.number}} {{invoice.total_formatted}} {{invoice.balance_formatted}} {{invoice.issue_date}} {{invoice.due_date}} {{invoice.public_url}} {{days_overdue}} {{company.upi_vpa}} {{items_table}}'}
            </span>
          </Alert>
          <Field label="Subject">
            <input type="text" value={String(editing.subject_tpl)} onChange={(e) => setEditing({ ...editing, subject_tpl: e.target.value })} />
          </Field>
          <Field label="Plain-text body">
            <textarea rows={12} value={String(editing.body_text_tpl)} onChange={(e) => setEditing({ ...editing, body_text_tpl: e.target.value })} />
          </Field>
          <Field label="HTML body">
            <textarea rows={8} className="mono" value={String(editing.body_html_tpl)} onChange={(e) => setEditing({ ...editing, body_html_tpl: e.target.value })} />
          </Field>
        </Modal>
      )}
    </>
  )
}

// ------------------------------------------------------------------- account

function Account() {
  const { user } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  return (
    <Card title="Your account">
      <Alert kind="error">{error}</Alert>
      <Alert kind="ok">{notice}</Alert>
      <p className="small muted" style={{ marginTop: 0 }}>
        Signed in as <strong>{user?.name}</strong> ({user?.email}) · role {user?.role}
      </p>
      <div className="sep" />
      <h3 style={{ marginBottom: 10 }}>Change password</h3>
      <div className="grid c2">
        <Field label="Current password">
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </Field>
        <Field label="New password" hint="At least 10 characters.">
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        </Field>
      </div>
      <button
        className="btn primary"
        onClick={async () => {
          setError('')
          setNotice('')
          try {
            await api.post('/auth/change-password', { currentPassword: current, newPassword: next })
            setNotice('Password changed.')
            setCurrent('')
            setNext('')
          } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Failed')
          }
        }}
      >
        Change password
      </button>
    </Card>
  )
}

// --------------------------------------------------------------- audit/jobs

function AuditAndJobs() {
  const [audit, setAudit] = useState<Record<string, string | number | null>[]>([])
  const [jobs, setJobs] = useState<Record<string, string | number | null>[]>([])

  useEffect(() => {
    api.get<{ log: Record<string, string | number | null>[] }>('/settings/audit').then((r) => setAudit(r.log))
    api.get<{ runs: Record<string, string | number | null>[] }>('/settings/jobs').then((r) => setJobs(r.runs))
  }, [])

  return (
    <>
      <Card title="Scheduled job runs" tight>
        {jobs.length === 0 ? (
          <Empty title="No job runs yet" hint="Cron fires every 15 minutes once deployed." />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Job</th>
                <th>Slot</th>
                <th>Status</th>
                <th>Detail</th>
                <th>Started</th>
              </tr>
            </thead>
            <tbody>
              {jobs.slice(0, 30).map((j) => (
                <tr key={String(j.id)}>
                  <td>{String(j.job)}</td>
                  <td className="small">{String(j.ran_on)}</td>
                  <td>
                    <Badge status={j.status === 'ok' ? 'paid' : j.status === 'error' ? 'overdue' : 'draft'}>
                      {String(j.status)}
                    </Badge>
                  </td>
                  <td className="small mono">{String(j.detail ?? '').slice(0, 90)}</td>
                  <td className="small nowrap">{String(j.started_at).slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div style={{ height: 14 }} />

      <Card title="Audit log" tight>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Entity</th>
                <th>Action</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {audit.slice(0, 60).map((a) => (
                <tr key={String(a.id)}>
                  <td className="small nowrap">{String(a.at).slice(0, 16).replace('T', ' ')}</td>
                  <td className="small">{String(a.user_email ?? '—')}</td>
                  <td className="small">{String(a.entity)}</td>
                  <td className="small">
                    <Badge status="neutral">{String(a.action)}</Badge>
                  </td>
                  <td className="small mono">{String(a.diff_json ?? '').slice(0, 80)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  )
}
